export type WidgetKey =
  | "net-pnl"
  | "profit-factor"
  | "win-rate"
  | "avg-win-loss"
  | "calendar"
  | "equity-chart"
  | "market-hours"
  | "routines"
  | "goals"
  | "session"
  | "accounts-table"
  | "analytics-kpis"
  | "equity-curve"
  | "pnl-distribution"
  | "monthly-pnl"
  | "symbol-performance"
  | "rolling-win-rate"
  | "heatmap"
  | "behavioral-biases"
  | "session-time-slots"
  | "session-mood"
  | "session-duration"
  | "session-pnl-chart"
  | "outside-session"
  | "session-insight"
  | "sessions-list"
  | "pip-stats"
  | "pip-distribution"
  | "pip-by-symbol"
  | "long-short"
  | "day-of-week"
  | "pnl-per-trade"
  | "net-daily-pnl"
  | "risk-adjusted"
  | "drawdown-curve"
  | "hold-duration"
  | "consistency-score"
  | "streak-analysis"
  | "tag-stats"
  | "sizing-analysis"
  | "propfirm-kpis"
  | "propfirm-calendar"
  | "propfirm-month-recap"
  | "propfirm-reporting"
  | "propfirm-accounts"
  | "propfirm-payouts-list"
  | "propfirm-filters"
  | "propfirm-wallet-balance"
  | "propfirm-wallet-transfers"
  | "propfirm-wallet-withdrawals"
  | "propfirm-accounts-table"
  | "propfirm-global-charges"
  | "propfirm-upcoming-charges"
  | "propfirm-challenge-overview"
  | "note"
  | "mentor-notes"
  | "topbar-date-filter"
  | "topbar-account-filter"
  | "topbar-strategy-filter"
  | "topbar-add-trade"
  | "topbar-add-payout"
  | "topbar-pfm-filter"
  | "topbar-phase-filter"
  | "news-feed"
  | "interest-rates"
  | "economic-calendar"
  | "earnings-calendar"
  | "chart"
  | "trades-table"
  | "topbar-symbol-filter"
  | "topbar-side-filter"
  | "topbar-status-filter"
  | "topbar-pnl-filter"
  | "topbar-tags-filter"
  | "last-session"
  | "discipline-routines"
  | "discipline-goals"
  | "notebook-browser";

export type LayoutScope = "dashboard" | "pfm" | "custom-page" | "journal" | "analytics" | "sessions" | "discipline";

export type ColSpan = 1 | 2 | 3 | 4 | 5 | 6;
export type RowSpan = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

/** A widget placed on the global grid. colStart/rowStart are 1-indexed. */
export interface SlotPlacement {
  id: string;
  widgetKey: WidgetKey;
  colStart: number;
  rowStart: number;
  colSpan: number;
  rowSpan: number;
  notebookNoteId?: string;
  placement?: "grid" | "topbar";
  defaultFilterValue?: string | null;
  viewMode?: "chart" | "table";
}

/** The full layout: a flat list of widget placements on a 6-column global grid. */
export interface LayoutSnapshot {
  slots: SlotPlacement[];
}

/** Props injected per-slot into chart widgets to drive the chart/table view toggle. */
export interface WidgetViewProps {
  viewMode?: "chart" | "table";
  onSetViewMode?: (mode: "chart" | "table") => void;
}

export interface WidgetRegistryEntry {
  key: WidgetKey;
  scope: LayoutScope | "both";
  minColSpan: 1 | 2 | 3 | 4 | 5 | 6;
  maxColSpan: 1 | 2 | 3 | 4 | 5 | 6;
  defaultColSpan: number;
  minRowSpan: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
  defaultRowSpan: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
  maxRowSpan: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
  /** Height in px of one row unit. Total widget height = rowSpan × rowHeight. */
  rowHeight: number;
  /** If set, the widget is locked behind this plan tier. */
  requiredPlan?: "Pro" | "Max";
}

/** Number of columns in the global grid. Fixed at 6. */
export const GRID_COLS = 6;

/** Height in px of one grid row track. */
export const GRID_ROW_HEIGHT = 150;
